import { Helmet, HelmetData } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import {
  MOTION_OK,
  revealTrigger,
  riseFrom,
  maskedLineReveal,
} from "../../../utils/animations";
import {
  AI_BG_NEW,
  Context_Awareness_Icon,
  Effiency_Icon,
  Financial_Choose,
  Financial_Deliver,
  Financial_Market_Hero,
  Innovative_Img,
  Our_Product_Bg,
} from "../../../assets";
const helmetData = new HelmetData({});

const why = [
  {
    icon: Innovative_Img,
    title: "Customer Intelligence",
    text: "Unify behavioural and transactional data to surface real-time insights that improve acquisition, retention, and service quality.",
  },
  {
    icon: Context_Awareness_Icon,
    title: "Risk & Compliance Analytics",
    text: "Deploy secure AI agents that detect anomalies, predict exposure, and generate explainable audit trails for governance teams.",
  },
  {
    icon: Effiency_Icon,
    title: "Operational Efficiency",
    text: "Automate analysis, reporting, and decision flows with LLM-powered agents that adapt to institutional logic and controls.",
  },
];

export default function FinancialMarketsPage() {
  const pageRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        maskedLineReveal(pageRef.current.querySelector(".fm-hero-heading"), {
          scrollTrigger: revealTrigger(".fm-hero-heading"),
        });
        riseFrom(".fm-hero-item", {
          delay: 0.2,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".fm-hero-item", "top 85%"),
        });
        riseFrom(".fm-hero-media", {
          y: 40,
          duration: 1.1,
          delay: 0.3,
          scrollTrigger: revealTrigger(".fm-hero-media", "top 85%"),
        });
        riseFrom(".fm-what-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".fm-what-row", "top 75%"),
        });
        riseFrom(".fm-what-copy > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".fm-what-row", "top 75%"),
        });
        riseFrom(".fm-choose-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".fm-choose-row", "top 75%"),
        });
        riseFrom(".fm-choose-copy > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".fm-choose-row", "top 75%"),
        });
        riseFrom(".fm-build-head", {
          scrollTrigger: revealTrigger(".fm-build-head"),
        });
        riseFrom(".fm-build-card", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".fm-build-grid", "top 75%"),
        });
        riseFrom(".fm-cta-item", {
          stagger: 0.12,
          scrollTrigger: revealTrigger(".fm-cta", "top 85%"),
        });
      });
      return () => mm.revert();
    },
    { scope: pageRef }
  );

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>AI for Financial Institutions | Analytics Intelligence Ltd — Secure AI &amp; Decision Intelligence</title>
        <link rel="canonical" href="https://analyticsintelligence.com/solutions/financial-markets" />
        <meta
          name="description"
          content="Analytics Intelligence Ltd empowers financial institutions with secure AI infrastructure — LLM-powered insights, predictive analytics for risk management, AI fraud detection, and regulatory reporting automation for banks, insurers, and fintechs."
        />
        <meta
          name="keywords"
          content="Analytics Intelligence, Analytics Intelligence Ltd, Analytics Intelligence Banking, Analytics Intelligence Fintech, AI for banking, machine learning for banks, AI compliance for financial institutions, predictive analytics for risk management, AI fraud detection platform Africa, regulatory reporting automation with AI, data governance solutions for banks, AI infrastructure for banks in Africa, banking analytics solutions in Mauritius, banking AI SSA, secure AI infrastructure, financial services AI, LLM insights, behavioural analytics, enterprise analytics platform, AI company in Mauritius, fintech AI solutions Mauritius"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta property="og:title" content="AI for Financial Institutions | Analytics Intelligence Ltd" />
        <meta property="og:description" content="Secure AI infrastructure for banks, insurers, and fintechs — LLM insights, risk analytics, fraud detection, and regulatory automation by Analytics Intelligence Ltd." />
        <meta property="og:url" content="https://analyticsintelligence.com/solutions/financial-markets" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://analyticsintelligence.com/favicon.png" />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta name="twitter:title" content="AI for Financial Institutions | Analytics Intelligence Ltd" />
        <meta name="twitter:description" content="Secure AI infrastructure for banks, insurers, and fintechs — LLM insights, risk analytics, fraud detection, and regulatory automation by Analytics Intelligence Ltd." />
        <meta name="twitter:image" content="https://analyticsintelligence.com/favicon.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "AI Solutions for Financial Institutions",
            "serviceType": "AI Infrastructure & Analytics for Financial Services",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Ltd",
              "url": "https://analyticsintelligence.com"
            },
            "description": "Analytics Intelligence Ltd provides secure AI infrastructure, predictive analytics for risk management, AI fraud detection, and regulatory reporting automation for banks, central banks, commercial banks, insurers, and fintechs across Africa and globally.",
            "areaServed": ["Mauritius", "Africa", "United Kingdom", "Europe", "North America", "South America", "Australia"],
            "url": "https://analyticsintelligence.com/solutions/financial-markets"
          }
        `}</script>
      </Helmet>

      <main ref={pageRef}>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          className="min-h-[90vh] flex items-center"
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16  pt-[126px] md:pt-[96px] py-[96px]">
            <div className="flex basis-3/5 flex-col flex-wrap gap-[16px] items-center md:items-start">
              <div className="space-y-4 flex flex-col items-center md:items-start">
                <span className="fm-hero-item text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  Financial Institutions
                </span>
                <h2
                  className="fm-hero-heading text-[36px] md:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white"
                  dangerouslySetInnerHTML={{
                    __html:
                      "Secure AI Infrastructure for Financial Services Intelligence",
                  }}
                />
                <p className="fm-hero-item text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                  Enable LLM-powered insights, risk evaluation, and operational
                  decision support by activating behavioural, transactional, and
                  customer interaction data across your FS organisation.
                </p>
              </div>
            </div>
            <div className="basis-[40%] flex justify-end">
              <img
                className="fm-hero-media w-full"
                src={Financial_Market_Hero}
                alt="illustration"
              />
            </div>
          </div>
        </section>

        <section
          className="bg-[#010127] relative flex flex-col space-y-[96px] pb-[96px]"
          id="use-cases"
        >
          <div className="w-[150px] lg:w-[400px] h-[400px] bg-[#0073FF]/70 blur-[150px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"></div>
          <div className="fm-what-row container flex flex-col-reverse md:flex-row items-center mt-11 gap-8 z-[100]">
            <div className="flex-1 basis-2/4 flex items-center justify-center">
              <img
                src={Financial_Deliver}
                alt="Data Structures"
                className="fm-what-media w-full md:max-w-[400px] h-auto"
              />
            </div>

            <div className="fm-what-copy flex-1 basis-2/4 space-y-4">
              <h2 class="text-2xl md:text-[32px] font-extrabold text-white text-center md:text-left">
                What is our Financial Institutions Solution?
              </h2>

              <div class="space-y-2 mb-12">
                <p className="text-sm md:text-base text-white">
                  Our solution for financial institutions provides a secure AI
                  data infrastructure for collecting, processing, and
                  operationalising customer behavioural and transactional data
                  at scale. Banks, insurers, lenders, and payment providers can
                  deploy analytics agents that reason with real-world signals,
                  improving customer outcomes, risk decisions, operational
                  efficiency, and insight delivery.
                </p>

                <p className="text-sm md:text-base text-white">
                  This solution combines:
                </p>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">Pulse: </span>
                    <span class="text-white ml-1">
                      behavioural data tracking across channels, journeys,
                      applications, and servicing flows
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">
                      Secure Data Pipelines:
                    </span>
                    <span class="text-white ml-1">
                      Ingestion and governance of transactional, product, and
                      customer interaction signals
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">MCP Server: </span>
                    <span class="text-white ml-1">
                      Contextual enrichment enabling models to leverage
                      structured & unstructured context for improved reasoning
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">PLM: </span>
                    <span class="text-white ml-1">
                      Private models adapted to financial terminology and
                      product-specific nuance
                    </span>
                  </div>
                </div>
                <p className="text-sm md:text-base text-white">
                  Either together or individually, this foundation enables
                  enterprises to operationalise AI with confidence, control, and
                  end-to-end security.
                </p>
              </div>
            </div>
          </div>

          <div
            className="fm-choose-row container flex flex-col-reverse md:flex-row-reverse items-center mt-11 gap-8"
            style={{ zIndex: 100 }}
          >
            <div className="flex-1 basis-2/4 flex items-center justify-center">
              <img
                src={Financial_Choose}
                alt="Data Structures"
                className="fm-choose-media w-full md:max-w-[400px] h-auto"
              />
            </div>

            <div className="fm-choose-copy flex-1 basis-2/4 space-y-4">
              <h2 class="text-2xl md:text-[32px] font-extrabold text-white text-center md:text-left">
                Why Financial Institutions Choose Us
              </h2>

              <div class="space-y-2 mb-12">
                <p className="text-sm md:text-base text-white">
                  Financial institutions require AI that is secure, auditable,
                  explainable, and trustworthy.
                </p>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">
                      Regulated by Design:
                    </span>
                    <span class="text-white ml-1">
                      Deployed within secure, compliant architecture aligned to
                      industry standards
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">
                      Insight Transparency:
                    </span>
                    <span class="text-white ml-1">
                      Traceable reasoning so analysts and governance teams can
                      validate outcomes
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">
                      Risk-Aware Intelligence:
                    </span>
                    <span class="text-white ml-1">
                      LLMs reason with behaviour, signals, and context instead
                      of static reports
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">
                      Operational Lift:
                    </span>
                    <span class="text-white ml-1">
                      Faster analytics cycles, improved customer decisioning,
                      and higher productivity
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#EDF1FF] py-[48px]">
          <div className="container space-y-12">
            <h2 className="fm-build-head text-[#020128] font-extrabold text-[24px] md:text-[32px] text-center">
              What we can build for you 
            </h2>

            <div className="fm-build-grid flex justify-center flex-wrap gap-11">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className="fm-build-card group w-full lg:w-[45%] xl:max-w-[400px] bg-white hover:bg-poppingBlue flex flex-col items-center rounded-xl px-3 py-6 gap-4 transition-colors duration-700"
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16 transition-all duration-700 group-hover:brightness-0 group-hover:invert"
                  />
                  <div className="space-y-1">
                    <h5 className="text-center text-base md:text-lg font-bold text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.title}
                    </h5>
                    <p className="text-sm md:text-base text-[#020128] text-center transition-colors duration-700 group-hover:text-white">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="bg-[#010127] relative py-[116px]"
          style={{
            backgroundImage: `url("${Our_Product_Bg}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        >
          <div className="fm-cta container flex flex-col items-center gap-6">
            <div className="fm-cta-item">
              <h2 className="text-[32px] md:text-[44px] text-center font-extrabold text-white">
                Work With Confidence
              </h2>
              <p className="text-sm md:text-base text-center text-[#FFFFFF]">
                We build AI where privacy, auditability and precision are
                first‑class — not an afterthought.
              </p>
            </div>

            <Link className="fm-cta-item" to="/contact">
              <button className="text-sm md:text-base font-semibold py-3 w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2">
                Talk to an Expert
              </button>
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}
